import CaretDownOutlined from "@ant-design/icons/CaretDownOutlined";
import FallOutlined from "@ant-design/icons/FallOutlined";
import RiseOutlined from "@ant-design/icons/RiseOutlined";
import { typography } from "@shared/common/Themes/typography";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { hexToRGB } from "@shared/utils/misc";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Progress from "common/Progress/Progress";
import AutoSizeText from "common/Text/AutoSizeText";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import Text from "common/Typography/Text";
import { useMemo, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import { ChevronDown, TrendingDown, TrendingUp } from "lucide-react";
import Panel from "common/NewLook/Panel.tsx";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import Styles from "features/configuration/common/styles";
import { round } from "@shared/utils/numbers.ts";
import {
  ACCOUNT_EXPOSURE_CARD_ID,
  ACCOUNT_EXPOSURE_CORNIX_CURRENT_SECTION_ID,
  ACCOUNT_EXPOSURE_CORNIX_POTENTIAL_SECTION_ID,
  ACCOUNT_EXPOSURE_CORNIX_SECTION_ID,
  ACCOUNT_EXPOSURE_CORNIX_SECTION_TITLE_ID,
  ACCOUNT_EXPOSURE_CORNIX_TOGGLE_ID,
  ACCOUNT_EXPOSURE_EXCHANGE_SECTION_ID,
  ACCOUNT_EXPOSURE_TILE_BTC_VALUE_ID,
  ACCOUNT_EXPOSURE_TILE_ID,
  ACCOUNT_EXPOSURE_TILE_LABEL_ID,
  ACCOUNT_EXPOSURE_TILE_USD_VALUE_ID,
  ACCOUNT_EXPOSURE_TITLE_ID,
} from "testIds.ts";

interface AccountExposureData {
  exchange_long_exposure?: ExposureAmount;
  exchange_short_exposure?: ExposureAmount;
  cornix_long_current_exposure?: ExposureAmount;
  cornix_short_current_exposure?: ExposureAmount;
  cornix_long_potential_exposure?: ExposureAmount;
  cornix_short_potential_exposure?: ExposureAmount;
}

const ExposureCard = ({ data }: { data?: AccountExposureData }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const theme = useTheme();

  if (theme.isNewLook) return <NewLookExposureCard data={data} />;

  return (
    <Styles.CardsWrapper
      style={{ maxWidth: "100%" }}
      data-testid={ACCOUNT_EXPOSURE_CARD_ID}
    >
      <Header>
        <Text
          $weight={theme.typography.fontWeights.BOLD}
          $size="L"
          data-testid={ACCOUNT_EXPOSURE_TITLE_ID}
        >
          {t("exchange_exposure.title")}
        </Text>
      </Header>
      <ExposurePair
        sectionId={ACCOUNT_EXPOSURE_EXCHANGE_SECTION_ID}
        long={getExposureAmount(data?.exchange_long_exposure)}
        short={getExposureAmount(data?.exchange_short_exposure)}
      />
      <CornixExposureAccordion data={data} />
    </Styles.CardsWrapper>
  );
};

const ExposurePair = ({
  sectionId,
  long,
  short,
  compact,
}: ExposurePairProps) => {
  const { longPercent, shortPercent } = getExposureRatio(long.USD, short.USD);
  const showPercent = long.USD + short.USD > 0;

  return (
    <div>
      <TilesGrid>
        <ExposureTile
          sectionId={sectionId}
          side="long"
          amount={long}
          percent={longPercent}
          showPercent={showPercent}
          compact={compact}
        />
        <ExposureTile
          sectionId={sectionId}
          side="short"
          amount={short}
          percent={shortPercent}
          showPercent={showPercent}
          compact={compact}
        />
      </TilesGrid>
      <ExposureRatioBar longPercent={longPercent} />
    </div>
  );
};

type ExposureSide = "long" | "short";

type ExposureAmount = {
  USD: number;
  BTC: number;
};

type ExposureSectionId =
  | typeof ACCOUNT_EXPOSURE_EXCHANGE_SECTION_ID
  | typeof ACCOUNT_EXPOSURE_CORNIX_CURRENT_SECTION_ID
  | typeof ACCOUNT_EXPOSURE_CORNIX_POTENTIAL_SECTION_ID;
type CornixExposureSectionId = Exclude<
  ExposureSectionId,
  typeof ACCOUNT_EXPOSURE_EXCHANGE_SECTION_ID
>;
type CornixExposureSection = {
  id: CornixExposureSectionId;
  title: string;
  tooltipTitle: string;
  tooltipDescription: string;
  long: ExposureAmount;
  short: ExposureAmount;
};

interface ExposurePairProps {
  sectionId: ExposureSectionId;
  long: ExposureAmount;
  short: ExposureAmount;
  compact?: boolean;
}

const EMPTY_EXPOSURE_AMOUNT: ExposureAmount = { USD: 0, BTC: 0 };

const formatUsd = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatBtc = (value: number) => `${value.toFixed(6)} BTC`;

const getExposureRatio = (longUsd: number, shortUsd: number) => {
  const total = longUsd + shortUsd;

  return {
    longPercent: total > 0 ? (longUsd / total) * 100 : 0,
    shortPercent: total > 0 ? (shortUsd / total) * 100 : 0,
  };
};

const getExposureAmount = (amount?: ExposureAmount) =>
  amount ?? EMPTY_EXPOSURE_AMOUNT;

const ExposureTile = ({
  sectionId,
  side,
  amount,
  percent,
  showPercent,
  compact,
}: {
  sectionId: ExposureSectionId;
  side: ExposureSide;
  amount: ExposureAmount;
  percent: number;
  showPercent: boolean;
  compact?: boolean;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const theme = useTheme();
  const isZero = amount.USD === 0 && amount.BTC === 0;
  const DirectionIcon = side === "long" ? RiseOutlined : FallOutlined;
  const roundedPercent = round(percent, 1);

  return (
    <Tile
      $side={side}
      $compact={compact}
      $muted={side === "short" && isZero}
      data-testid={`${ACCOUNT_EXPOSURE_TILE_ID}-${sectionId}-${side}`}
    >
      <LabelRow
        $compact={compact}
        data-testid={`${ACCOUNT_EXPOSURE_TILE_LABEL_ID}-${sectionId}-${side}`}
      >
        <DirectionIcon />
        {t(`exchange_exposure.${side}`)}
        {showPercent && (
          <LabelPercent $compact={compact}> {roundedPercent}%</LabelPercent>
        )}
      </LabelRow>
      <UsdValue
        $compact={compact}
        data-testid={`${ACCOUNT_EXPOSURE_TILE_USD_VALUE_ID}-${sectionId}-${side}`}
      >
        <AutoSizeText
          baseFontSizePx={typography.pxFontSizes.XL}
          fontWeight={theme.typography.fontWeights.BOLD}
          style={{ lineHeight: 1.15 }}
        >
          {formatUsd(amount.USD)}
        </AutoSizeText>
      </UsdValue>
      <BtcValue
        $compact={compact}
        data-testid={`${ACCOUNT_EXPOSURE_TILE_BTC_VALUE_ID}-${sectionId}-${side}`}
      >
        {formatBtc(amount.BTC)}
      </BtcValue>
    </Tile>
  );
};

const ExposureRatioBar = ({ longPercent }: { longPercent: number }) => {
  return (
    <RatioBarTrack>
      <StyledProgress
        percent={longPercent}
        showInfo={false}
        strokeLinecap="butt"
        status="normal"
      />
    </RatioBarTrack>
  );
};

const CornixExposureAccordion = ({ data }: { data?: AccountExposureData }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const [expanded, setExpanded] = useState(true);
  const theme = useTheme();

  const sections = useMemo<CornixExposureSection[]>(
    () => [
      {
        id: ACCOUNT_EXPOSURE_CORNIX_CURRENT_SECTION_ID,
        title: t("exchange_exposure.filled"),
        tooltipTitle: t("exchange_exposure.active_helper_title"),
        tooltipDescription: t("exchange_exposure.active_helper_description"),
        long: getExposureAmount(data?.cornix_long_current_exposure),
        short: getExposureAmount(data?.cornix_short_current_exposure),
      },
      {
        id: ACCOUNT_EXPOSURE_CORNIX_POTENTIAL_SECTION_ID,
        title: t("exchange_exposure.allocated"),
        tooltipTitle: t("exchange_exposure.potential_helper_title"),
        tooltipDescription: t("exchange_exposure.potential_helper_description"),
        long: getExposureAmount(data?.cornix_long_potential_exposure),
        short: getExposureAmount(data?.cornix_short_potential_exposure),
      },
    ],
    [data, t]
  );

  return (
    <div>
      <AccordionButton
        $expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
        data-testid={ACCOUNT_EXPOSURE_CORNIX_TOGGLE_ID}
      >
        <Text
          $weight={theme.typography.fontWeights.BOLD}
          $size="L"
          type="secondary"
        >
          {t("exchange_exposure.cornix_title")}
        </Text>
        <AccordionIcon $expanded={expanded} />
      </AccordionButton>
      <ExpandCollapse expanded={expanded}>
        <CornixContent>
          {sections.map((section) => (
            <div
              key={section.id}
              data-testid={`${ACCOUNT_EXPOSURE_CORNIX_SECTION_ID}-${section.id}`}
            >
              <CornixSectionTitle>
                <Text
                  $weight={theme.typography.fontWeights.BOLD}
                  $size="L"
                  type="secondary"
                  data-testid={`${ACCOUNT_EXPOSURE_CORNIX_SECTION_TITLE_ID}-${section.id}`}
                >
                  {section.title}
                </Text>
                <HelperTooltip
                  title={section.tooltipTitle}
                  description={section.tooltipDescription}
                  analyticsPageName="Accounts"
                  analyticsFieldName={section.tooltipTitle}
                  containerStyle={{ marginLeft: 8 }}
                  iconStyle={{ color: theme.colors.secondaryText }}
                />
              </CornixSectionTitle>
              <ExposurePair
                sectionId={section.id}
                long={section.long}
                short={section.short}
                compact={true}
              />
            </div>
          ))}
        </CornixContent>
      </ExpandCollapse>
    </div>
  );
};

export default ExposureCard;

const useCornixSections = (data?: AccountExposureData) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  return useMemo<CornixExposureSection[]>(
    () => [
      {
        id: ACCOUNT_EXPOSURE_CORNIX_CURRENT_SECTION_ID,
        title: t("exchange_exposure.filled"),
        tooltipTitle: t("exchange_exposure.active_helper_title"),
        tooltipDescription: t("exchange_exposure.active_helper_description"),
        long: getExposureAmount(data?.cornix_long_current_exposure),
        short: getExposureAmount(data?.cornix_short_current_exposure),
      },
      {
        id: ACCOUNT_EXPOSURE_CORNIX_POTENTIAL_SECTION_ID,
        title: t("exchange_exposure.allocated"),
        tooltipTitle: t("exchange_exposure.potential_helper_title"),
        tooltipDescription: t("exchange_exposure.potential_helper_description"),
        long: getExposureAmount(data?.cornix_long_potential_exposure),
        short: getExposureAmount(data?.cornix_short_potential_exposure),
      },
    ],
    [data, t]
  );
};

/**
 * New look: one panel; each pair is long and short side by side (micro
 * label, figure, BTC meta) over a split bar in the direction colours.
 */
const NewLookExposureCard = ({ data }: { data?: AccountExposureData }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const tokens = getNewLookTokens(useTheme());
  const [expanded, setExpanded] = useState(true);
  const sections = useCornixSections(data);

  return (
    <Panel
      flush={true}
      data-testid={ACCOUNT_EXPOSURE_CARD_ID}
      title={
        <span data-testid={ACCOUNT_EXPOSURE_TITLE_ID}>
          {t("exchange_exposure.title")}
        </span>
      }
    >
      <NewLookPair
        sectionId={ACCOUNT_EXPOSURE_EXCHANGE_SECTION_ID}
        long={getExposureAmount(data?.exchange_long_exposure)}
        short={getExposureAmount(data?.exchange_short_exposure)}
      />
      <NewLookToggle
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((previous) => !previous)}
        data-testid={ACCOUNT_EXPOSURE_CORNIX_TOGGLE_ID}
      >
        {t("exchange_exposure.cornix_title")}
        <ChevronDown
          size={16}
          strokeWidth={NEW_LOOK_ICON_STROKE}
          style={{
            color: tokens.secondary,
            transform: `rotate(${expanded ? 0 : -90}deg)`,
            transition: "transform 200ms",
          }}
        />
      </NewLookToggle>
      <ExpandCollapse expanded={expanded}>
        {sections.map((section) => (
          <NewLookSection
            key={section.id}
            data-testid={`${ACCOUNT_EXPOSURE_CORNIX_SECTION_ID}-${section.id}`}
          >
            <NewLookSectionTitle>
              <span
                data-testid={`${ACCOUNT_EXPOSURE_CORNIX_SECTION_TITLE_ID}-${section.id}`}
              >
                {section.title}
              </span>
              <HelperTooltip
                title={section.tooltipTitle}
                description={section.tooltipDescription}
                analyticsPageName="Accounts"
                analyticsFieldName={section.tooltipTitle}
                iconStyle={{ color: tokens.muted }}
              />
            </NewLookSectionTitle>
            <NewLookPair
              sectionId={section.id}
              long={section.long}
              short={section.short}
              nested={true}
            />
          </NewLookSection>
        ))}
      </ExpandCollapse>
    </Panel>
  );
};

const NewLookPair = ({
  sectionId,
  long,
  short,
  nested,
}: Omit<ExposurePairProps, "compact"> & { nested?: boolean }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const tokens = getNewLookTokens(useTheme());
  const { longPercent, shortPercent } = getExposureRatio(long.USD, short.USD);
  const showPercent = long.USD + short.USD > 0;
  const sides: [ExposureSide, ExposureAmount, number][] = [
    ["long", long, longPercent],
    ["short", short, shortPercent],
  ];
  return (
    <NewLookPairWrapper $nested={nested}>
      <NewLookSides>
        {sides.map(([side, amount, percent]) => {
          const Icon = side === "long" ? TrendingUp : TrendingDown;
          return (
            <NewLookSide
              key={side}
              data-testid={`${ACCOUNT_EXPOSURE_TILE_ID}-${sectionId}-${side}`}
            >
              <NewLookSideLabel
                data-testid={`${ACCOUNT_EXPOSURE_TILE_LABEL_ID}-${sectionId}-${side}`}
              >
                <Icon
                  size={13}
                  strokeWidth={NEW_LOOK_ICON_STROKE}
                  style={{ color: side === "long" ? tokens.up : tokens.down }}
                />
                {t(`exchange_exposure.${side}`)}
                {showPercent && <span> {round(percent, 1)}%</span>}
              </NewLookSideLabel>
              <NewLookSideValue
                data-testid={`${ACCOUNT_EXPOSURE_TILE_USD_VALUE_ID}-${sectionId}-${side}`}
              >
                {formatUsd(amount.USD)}
              </NewLookSideValue>
              <NewLookSideMeta
                data-testid={`${ACCOUNT_EXPOSURE_TILE_BTC_VALUE_ID}-${sectionId}-${side}`}
              >
                {formatBtc(amount.BTC)}
              </NewLookSideMeta>
            </NewLookSide>
          );
        })}
      </NewLookSides>
      <NewLookSplit aria-hidden={true}>
        {showPercent ? (
          <>
            <span style={{ flexGrow: longPercent, background: tokens.up }} />
            <span style={{ flexGrow: shortPercent, background: tokens.down }} />
          </>
        ) : (
          <span style={{ flexGrow: 1, background: tokens.well }} />
        )}
      </NewLookSplit>
    </NewLookPairWrapper>
  );
};

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: ${(p) => p.theme.borders.lightest};
`;
const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
`;
const Tile = styled.div<{
  $side: ExposureSide;
  $compact?: boolean;
  $muted?: boolean;
}>`
  min-width: 0;
  padding: ${(p) => (p.$compact ? "14px 28px 12px" : "18px 28px 16px")};
  background: ${(p) =>
    p.$side === "long"
      ? hexToRGB(p.theme.colors.primaryBlue, p.theme.isDarkMode ? 0.13 : 0.08)
      : "transparent"};
  opacity: ${(p) => (p.$muted ? 0.72 : 1)};

  ${(p) =>
    p.$compact &&
    css`
      background: ${p.$side === "long"
        ? hexToRGB(p.theme.colors.primaryBlue, p.theme.isDarkMode ? 0.08 : 0.05)
        : "transparent"};
    `};

  @media (max-width: 400px) {
    padding: ${(p) => (p.$compact ? "12px 16px 10px" : "16px 18px 14px")};
  }
`;
const LabelRow = styled(Text).attrs((p) => ({
  as: "div",
  // New look: eyebrows and inline values are 500 (only big figures 600).
  $weight: p.theme.isNewLook
    ? p.theme.typography.fontWeights.MEDIUM
    : p.theme.typography.fontWeights.BOLD,
}))<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  letter-spacing: 0;
  text-transform: uppercase;
  font-size: ${(p) =>
    p.$compact
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
`;
const LabelPercent = styled(Text).attrs((p) => ({
  as: "span",
  $weight: p.theme.isNewLook
    ? p.theme.typography.fontWeights.MEDIUM
    : p.theme.typography.fontWeights.BOLD,
}))<{ $compact?: boolean }>`
  font-size: ${(p) =>
    p.$compact
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
`;
const UsdValue = styled.div<{ $compact?: boolean }>`
  margin-top: 4px;
`;
const BtcValue = styled(Text).attrs((p) => ({
  as: "div",
  type: "secondary",
  $weight: p.theme.isNewLook
    ? p.theme.typography.fontWeights.MEDIUM
    : p.theme.typography.fontWeights.BOLD,
}))<{ $compact?: boolean }>`
  margin-top: 6px;
  font-size: ${(p) => p.theme.typography.fontSizes.M};
  line-height: 1.15;
`;
const RatioBarTrack = styled.div`
  position: relative;
  height: 6px;
  overflow: hidden;
`;
const StyledProgress = styled(Progress)`
  display: block;
  height: 6px;
  line-height: 0;

  .ant-progress-outer,
  .ant-progress-inner {
    display: block;
    height: 6px !important;
    line-height: 0;
  }

  .ant-progress-bg {
    height: 6px !important;
  }
`;
const AccordionButton = styled.div<{ $expanded: boolean }>`
  width: 100%;
  border-bottom: ${(p) => (p.$expanded ? p.theme.borders.lightest : 0)};
  background: transparent;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  outline: none;

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${(p) => p.theme.colors.primaryBlue};
  }
`;
const AccordionIcon = styled(CaretDownOutlined)<{ $expanded: boolean }>`
  color: ${(p) => p.theme.colors.secondaryText};
  transform: rotate(${(p) => (p.$expanded ? 0 : -90)}deg);
  transition: transform 0.2s ease;
`;
const CornixContent = styled.div`
  padding-top: 0;
`;
const CornixSectionTitle = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 28px 10px;
`;

const NewLookPairWrapper = styled.div<{ $nested?: boolean }>`
  padding: ${(p) => (p.$nested ? "8px 20px 16px" : "16px 20px 20px")};
`;
const NewLookSides = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  > * + * {
    padding-left: 20px;
    border-left: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const NewLookSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
const NewLookSideLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const NewLookSideValue = styled.div`
  ${newLookType.figureLarge};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const NewLookSideMeta = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const NewLookSplit = styled.div`
  display: flex;
  gap: 2px;
  height: 6px;
  margin-top: 14px;
  border-radius: 3px;
  overflow: hidden;
  > span {
    flex-basis: 0;
    min-width: 0;
  }
`;
const NewLookToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 52px;
  padding: 10px 16px 10px 20px;
  border: 0;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
  }
`;
const NewLookSection = styled.div`
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const NewLookSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 12px 20px 0;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
